import { Box, Stack, Typography } from '@mui/material'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import { CommentVisibility } from '@omnea-app/fe-comments/commentInputTypes'
import { RequestFormParticipants } from '@omnea-app/requests/requestsHttpClient'
import { CommentSource } from '../../Comments'
import { CommentDefaultEmptyState } from './CommentDefaultEmptyState'
import { CommentEmptyMessageWithConfiguredText } from './CommentEmptyMessageWithConfiguredText'

interface CommentEmptyStateProps {
  commentSource: CommentSource
  requestFormParticipants?: RequestFormParticipants
  commentsVisibility: CommentVisibility
  hideImage?: boolean
}

export const CommentEmptyState = ({
  commentSource,
  commentsVisibility,
  requestFormParticipants,
  hideImage = false,
}: CommentEmptyStateProps) => {
  const { t } = useTranslation('requests')
  const isInternal = commentsVisibility === 'internal'

  const views = {
    request: {
      text: t('request-comments.no-comments'),
      imagePath: '/app/assets/no-comments-empty-state.svg',
      imageDimensions: { height: 60, width: 120 },
    },
    mention: {
      text: t('request-comments.no-comments-mentions'),
      imagePath: '/app/assets/mentions-empty-state.svg',
      imageDimensions: { height: 110, width: 110 },
    },
    submission: {
      text: t('request-comments.no-comments-submissions'),
      imagePath: '/app/assets/submissions-empty-state.svg',
      imageDimensions: { height: 110, width: 110 },
    },
    'dashboard-mentions-resolved': {
      text: t('request-comments.no-comments-dashboard-mentions.resolved'),
      imagePath: '/app/assets/mentions-empty-state.svg',
      imageDimensions: { height: 110, width: 110 },
    },
    'dashboard-mentions-unresolved': {
      text: t('request-comments.no-comments-dashboard-mentions.unresolved'),
      imagePath: '/app/assets/mentions-empty-state.svg',
      imageDimensions: { height: 110, width: 110 },
    },
    supplier: {
      text: t('request-comments.no-comments'),
      imagePath: '/app/assets/main-thread-empty-state.svg',
      imageDimensions: { height: 120, width: 270 },
    },
    risk: {
      text: t('request-comments.no-comments'),
      imagePath: '/app/assets/main-thread-empty-state.svg',
      imageDimensions: { height: 120, width: 270 },
    },
    'audit-log': {
      text: t('request-comments.no-comments-audit-log'),
      imagePath: '/app/assets/main-thread-empty-state.svg',
      imageDimensions: { height: 120, width: 270 },
    },
    agreement: {
      text: t('request-comments.no-comments'),
      imagePath: '/app/assets/main-thread-empty-state.svg',
      imageDimensions: { height: 120, width: 270 },
    },
    riskAlert: {
      text: t('request-comments.no-comments'),
      imagePath: '/app/assets/main-thread-empty-state.svg',
      imageDimensions: { height: 120, width: 270 },
    },
    bill: {
      text: t('request-comments.no-comments'),
      imagePath: '/app/assets/main-thread-empty-state.svg',
      imageDimensions: { height: 120, width: 270 },
    },
  }

  const view = views[commentSource]

  const EmptyStateToLoad = () => {
    if (isInternal) {
      return view.text
    }
    if (requestFormParticipants?.emptyStateMessage) {
      return <CommentEmptyMessageWithConfiguredText emptyCommentsMessage={requestFormParticipants?.emptyStateMessage} />
    }
    return <CommentDefaultEmptyState requestFormParticipants={requestFormParticipants} />
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 8, flex: 1 }}>
      <Stack gap={6} sx={{ textAlign: 'center' }} justifyContent="center">
        {!hideImage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <Image
              alt="No comments"
              src={view.imagePath}
              height={view.imageDimensions.height}
              width={view.imageDimensions.width}
            />
          </Box>
        )}
        <Typography color="text.muted" variant="body-regular">
          <EmptyStateToLoad />
        </Typography>
      </Stack>
    </Box>
  )
}
